import { InjectionToken } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

/**
 * Handle a dialog body uses to close itself and hand back a result.
 *
 * This exists so a consumer's dialog component never imports
 * `@angular/material/dialog`. Material is an implementation detail of this
 * library; a dialog written against `BzDialogRef` keeps working if that
 * changes, and a consuming application does not grow a direct Material
 * dependency in every dialog it writes.
 *
 * Why it is attached after construction rather than injected with the
 * Material ref already in hand: `MatDialogConfig` has no `providers` field
 * (CDK's `DialogConfig` does, Material's does not), so the only way to put a
 * token in front of a Material dialog body is a custom `injector` — and that
 * injector is built *before* `MatDialog.open()` exists to give us a ref. So
 * `BzDialogService` provides this instance empty and attaches the ref during
 * the same synchronous `open()` call that creates the body. The body cannot
 * observe the gap: nothing can click a button before the component exists.
 */
export class BzDialogRef<R = unknown> {
  private ref?: MatDialogRef<unknown, R>;

  /** Called by `BzDialogService`. Not part of the public contract. */
  attach(ref: MatDialogRef<unknown, R>): void {
    this.ref = ref;
  }

  /** Closes with a result. Omitting it means "dismissed", the same as Escape. */
  close(result?: R): void {
    this.assertAttached().close(result);
  }

  /** Blocks or allows closing by backdrop click and Escape, after opening. */
  setClosable(closable: boolean): void {
    this.assertAttached().disableClose = !closable;
  }

  private assertAttached(): MatDialogRef<unknown, R> {
    if (!this.ref) {
      // Only reachable from a field initialiser or constructor, which run
      // before the ref is attached. Better a named error than a silent no-op.
      throw new Error(
        'BzDialogRef used before the dialog finished opening. Call close() from an event handler or a lifecycle hook, not from a constructor or field initialiser.',
      );
    }
    return this.ref;
  }
}

/**
 * The data passed to `BzDialogService.open(component, { data })`.
 *
 * Inject it in a dialog body, typed at the call site — the token is shared by
 * every dialog and each one knows its own shape:
 *
 * ```ts
 * private readonly data = inject<EditNoteData>(BZ_DIALOG_DATA);
 * ```
 */
export const BZ_DIALOG_DATA = new InjectionToken<unknown>('BZ_DIALOG_DATA');

/**
 * Whether this dialog was opened with `{ resizable: true }`.
 *
 * Provided by `BzDialogService` so `bz-dialog-layout` can render its resize
 * grip without the caller having to pass the same flag twice — once to the
 * service and again as an input on the layout.
 */
export const BZ_DIALOG_RESIZABLE = new InjectionToken<boolean>('BZ_DIALOG_RESIZABLE', {
  factory: () => false,
});
